import styled from 'styled-components';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'utils/withRouter';
import { injectIntl, FormattedMessage } from 'react-intl';
import SidePanel from 'common/side-panel/side-panel';
import { CloseOutlined } from '@ant-design/icons';
import { Flex, Div, Button, DropdownButton } from 'ui';
import { Text, getIcon } from './alerts';

export const Divider = styled(Div)`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin: 0.5rem 0;
`;

const PriorityBlock = styled(Div)`
  margin-bottom: 18px;
`;

const AlertItem = styled(Flex)`
  padding: 0.25rem 0;
`;

const AlertText = styled(Div)`
  font-weight: ${p => p.theme.fontWeight.bolder};
  color: ${p => p.theme.colors.text};
  margin-left: 20px;
  flex: 1;
  cursor: pointer;
`;

const Buttons = styled(Flex)`
  > div:first-child {
    margin-right: 5px;
  }
`;

const NoData = styled(AlertItem)`
  margin-left: 20px;
`;

const AlertDescription = styled(AlertItem)`
  margin-left: 20px;
`;

const AlertsAll = ({ alerts, snoozeRequest, discardRequest, isSaving, history, intl }) => {
  const goToDashboard = () => {
    history.back();
  };

  const goToTimelineOrInfo = (type) => {
    if (type === 'BreakdownOrderAlert') {
      const timelinePath = `${history.location.pathname.replace(
        '/dashboard/alerts',
        '',
      )}/timeline/filter/breakdown`;
      history.push(timelinePath);
    } else if (type === 'LastModifiedAlert') {
      const infoPath = `${history.location.pathname.replace('/dashboard/alerts', '')}/info`;
      history.push(infoPath);
    } else if (type === 'PartsNotDelivered') {
      // eslint-disable-next-line max-len
      const timelinePath = `${history.location.pathname.replace('/dashboard/alerts', '')}/timeline/filter/alerts`;
      history.push(timelinePath);
    } else if (type === 'DiraOrder') {
      // eslint-disable-next-line max-len
      const timelinePath = `${history.location.pathname.replace('/dashboard/alerts', '')}/timeline/filter/alerts`;
      history.push(timelinePath);
    }
  };

  const getActionsButtons = a =>
    <Buttons jcsb>
      <DropdownButton
        iconClass='fa fa-clock'
        text={intl.formatMessage({ id: 'dashboard.alerts.SNOOZE_DAY' })}
        onClick={() => snoozeRequest({ id: a.id, seconds: 60 * 60 * 24 })}
        disabled={isSaving[a.id]}
        subItems={[
          {
            text: intl.formatMessage({ id: 'dashboard.alerts.WEEK' }),
            onClick: () => snoozeRequest({ id: a.id, seconds: 60 * 60 * 24 * 7 }),
          },
          {
            text: intl.formatMessage({ id: 'dashboard.alerts.MONTH' }),
            onClick: () => snoozeRequest({ id: a.id, seconds: 60 * 60 * 24 * 30 }),
          },
        ]}
      >
        <FormattedMessage id='dashboard.alerts.SNOOZE' />
      </DropdownButton>
      <Button
        type='secondary'
        icon={<CloseOutlined />}
        disabled={isSaving[a.id]}
        onClick={() => discardRequest({ id: a.id })}
      >
        <FormattedMessage id='dashboard.alerts.DISCARD' />
      </Button>
    </Buttons>;
  const highPrioAlerts = alerts?.filter(a => a.severity === 1);
  const medPrioAlerts = alerts?.filter(a => a.severity === 2);
  const lowPrioAlerts = alerts?.filter(a => a.severity === 3);

  return (
    <SidePanel
      width={'40%'}
      title={intl.formatMessage({ id: 'dashboard.alerts.TITLE' })}
      onPanelClose={goToDashboard}
    >
      <PriorityBlock>
        <Text severity={1}>
          {getIcon(1)} <FormattedMessage id='dashboard.alerts.HIGH_PRIO' /> ({highPrioAlerts.length}
          )
        </Text>
        <Divider />
        <>
          {highPrioAlerts.length ?
            highPrioAlerts?.map(a =>
              <div key={a.id}>
                <AlertItem key={a.id}>
                  <AlertText onClick={() => goToTimelineOrInfo(a.type)}>
                    {a.translatedName || a.name}
                  </AlertText>
                  {getActionsButtons(a)}
                </AlertItem>
                {a.description &&
                  <AlertDescription>{a.translatedDescription || a.description}</AlertDescription>
                }
              </div>,
            )
            :
            <NoData>
              <FormattedMessage id='dashboard.alerts.NO_DATA' />
            </NoData>
          }
        </>
      </PriorityBlock>
      <PriorityBlock>
        <Text severity={2}>
          {getIcon(2)} <FormattedMessage id='dashboard.alerts.MED_PRIO' /> ({medPrioAlerts.length})
        </Text>
        <Divider />
        <>
          {medPrioAlerts.length ?
            medPrioAlerts?.map(a =>
              <div key={a.id}>
                <AlertItem key={a.id}>
                  <AlertText onClick={() => goToTimelineOrInfo(a.type)}>{a.name}</AlertText>
                  {getActionsButtons(a)}
                </AlertItem>
                {a.description && <AlertDescription>{a.description}</AlertDescription>}
              </div>,
            )
            :
            <NoData>
              <FormattedMessage id='dashboard.alerts.NO_DATA' />
            </NoData>
          }
        </>
      </PriorityBlock>
      <PriorityBlock>
        <Text severity={3}>
          {getIcon(3)} <FormattedMessage id='dashboard.alerts.LOW_PRIO' /> ({lowPrioAlerts.length})
        </Text>
        <Divider />
        <>
          {lowPrioAlerts.length ?
            lowPrioAlerts?.map(a =>
              <div key={a.id}>
                <AlertItem key={a.id}>
                  <AlertText onClick={() => goToTimelineOrInfo(a.type)}>{a.name}</AlertText>
                  {getActionsButtons(a)}
                </AlertItem>
                {a.description && <AlertDescription>{a.description}</AlertDescription>}
              </div>,
            )
            :
            <NoData>
              <FormattedMessage id='dashboard.alerts.NO_DATA' />
            </NoData>
          }
        </>
      </PriorityBlock>
    </SidePanel>
  );
};

AlertsAll.propTypes = {
  alerts: PropTypes.array,
  snoozeRequest: PropTypes.func,
  discardRequest: PropTypes.func,
  isSaving: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object,
};

export default withRouter(injectIntl(AlertsAll));
